import * as React from "react"
import {
  createMongoAbility,
  type MongoAbility,
  type RawRuleOf,
} from "@casl/ability"
import {
  AbilityProvider as CaslAbilityProvider,
  useAbility as useCaslAbility,
} from "@casl/react"
import { useQuery } from "@tanstack/react-query"

import { authControllerAbilityOptions } from "@/client/@tanstack/react-query.gen"

export type AppAbility = MongoAbility

/**
 * The caller's CASL rules, as the backend builds them.
 *
 * Only steers the interface: what to show, where to link. Every request is
 * checked on the server again, so a tampered rule set opens nothing. Branch
 * on abilities rather than on `role === ...`, so the interface cannot drift
 * away from the rules in `casl-ability.factory.ts`.
 */
const emptyAbility: AppAbility = createMongoAbility()
// Separate from the ability: until the rules arrive everything reads as
// forbidden, and a guard must not redirect on that.
const AbilityReadyContext = React.createContext(false)

export function AbilityProvider({
  userId,
  viewAsTenantId,
  children,
}: {
  userId: string | undefined
  viewAsTenantId: string | null
  children: React.ReactNode
}) {
  const options = authControllerAbilityOptions()
  const { data, isFetched } = useQuery({
    ...options,
    // Keyed by who asks and where: a sign-in as someone else in the same tab,
    // or a switch into a tenant, must never reuse the previous rule set.
    queryKey: [...options.queryKey, userId ?? null, viewAsTenantId] as unknown as typeof options.queryKey,
    enabled: Boolean(userId),
    retry: false,
    staleTime: 60_000,
  })
  const ability = React.useMemo(
    () =>
      data
        ? createMongoAbility(data.rules as RawRuleOf<AppAbility>[])
        : emptyAbility,
    [data]
  )
  return (
    <AbilityReadyContext.Provider value={isFetched}>
      <CaslAbilityProvider value={ability}>{children}</CaslAbilityProvider>
    </AbilityReadyContext.Provider>
  )
}

export function useAbility(): AppAbility {
  return useCaslAbility<AppAbility>()
}

export function useAbilityReady(): boolean {
  return React.useContext(AbilityReadyContext)
}

export { Can } from "@casl/react"
